"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncHandler = exports.implementation = void 0;
/* eslint-disable no-useless-constructor,@typescript-eslint/no-this-alias */
const events_typed_1 = require("@video/events-typed");
const log_client_1 = require("@video/log-client");
const mobx_1 = require("mobx");
const logger_1 = require("../logger");
const events_handler_1 = require("./events-handler");
class SyncedMethod {
    constructor(key, _fn) {
        this.key = key;
        this._fn = _fn;
        this.stack = [];
    }
    get wrapper() {
        const self = this;
        const wrapper = function wrapper(...args) {
            return self.apply(this, args);
        };
        Object.defineProperty(wrapper, "name", { value: `${String(this.key)}SyncWrapper` });
        return wrapper;
    }
    get fn() {
        return this._fn;
    }
    set fn(fn) {
        this._fn = fn;
        if (fn != null) {
            this.stack.forEach(([self, args, resolve, reject]) => {
                fn.apply(self, args).then(resolve, reject);
            });
            this.stack.splice(0, this.stack.length);
        }
    }
    postpone(self, args) {
        return new Promise((resolve, reject) => {
            this.stack.push([self, args, resolve, reject]);
        });
    }
    apply(self, args) {
        if (this.fn != null) {
            return this.fn.apply(self, args);
        }
        return this.postpone(self, args);
    }
}
exports.implementation = Symbol("implementation");
class SyncHandler extends events_handler_1.EventsHandler {
    constructor(options = {}) {
        var _a;
        super();
        this.options = options;
        this.implementation = new WeakMap();
        this.syncProperties = new WeakMap();
        this.logger = (_a = options.logger) !== null && _a !== void 0 ? _a : new log_client_1.LoggerCore("SyncHandler");
    }
    sync(target, meta, key, impl) {
        var _a;
        if (impl != null && Reflect.get((_a = this.options.methods) !== null && _a !== void 0 ? _a : {}, key) === "postpone") {
            meta.method.fn = Reflect.get(impl, key);
            return;
        }
        if (impl != null && meta.changed) {
            // pass all accumulated values to new instance
            // if it was made by client
            Reflect.set(impl, key, meta.val);
        }
        // update mobx reaction if needed
        this.updateReaction(target, meta, key, impl);
        // change value and report that
        const newVal = impl == null ? undefined : Reflect.get(impl, key);
        if (!meta.changed && meta.val !== newVal) {
            meta.val = newVal;
            meta.atom.reportChanged();
        }
    }
    updateReaction(target, meta, key, impl) {
        var _a;
        // clean up
        (_a = meta.disposer) === null || _a === void 0 ? void 0 : _a.call(meta);
        delete meta.disposer;
        if (impl != null) {
            // reaction can be not observable here actually
            // but we can't check that, so we'll try anyway
            meta.disposer = (0, mobx_1.reaction)(() => Reflect.get(impl, key), (val) => {
                // vvv disabled for now because it requires players refactoring vvv
                //
                // if (meta.changed) {
                //   const targetClass = isNamedClass(target.constructor)
                //     ? target.constructor.displayName
                //     : target.constructor.name;
                //   const proxyClass = isNamedClass(impl.constructor) ? impl.constructor.displayName : impl.constructor.name;
                //   this.logger.warn("Attempts to update the value which was changed by implementer", {
                //     targetClass,
                //     proxyClass,
                //     prop: String(key),
                //   });
                //   // to prevent spreading this value
                //   // to another implementations
                //   meta.changed = false;
                //   Reflect.set(impl, key, meta.val);
                //   return;
                // }
                meta.val = val;
                meta.atom.reportChanged();
            }, { name: `proxy:${String(key)}` });
        }
    }
    propBecomeObservable(target, key) {
        var _a, _b;
        const meta = this.getOrCrateMeta(target, key);
        this.updateReaction(target, meta, key, (_b = (_a = this.implementation.get(target)) === null || _a === void 0 ? void 0 : _a.impl) !== null && _b !== void 0 ? _b : null);
    }
    propBecomeUnobservable(target, key) {
        var _a;
        const meta = this.getOrCrateMeta(target, key);
        (_a = meta.disposer) === null || _a === void 0 ? void 0 : _a.call(meta);
        delete meta.disposer;
    }
    getOrCrateMeta(target, key) {
        var _a, _b, _c;
        if (!this.syncProperties.has(target)) {
            this.syncProperties.set(target, new Map());
        }
        let obj = (_a = this.syncProperties.get(target)) === null || _a === void 0 ? void 0 : _a.get(key);
        if (obj == null) {
            const impl = (_b = this.implementation.get(target)) === null || _b === void 0 ? void 0 : _b.impl;
            const implVal = impl == null ? undefined : (0, mobx_1.untracked)(() => Reflect.get(impl, key));
            obj = {
                // use `untracked` way to get this value
                // because we don't want to trigger autorun twice
                val: implVal,
                atom: (0, mobx_1.createAtom)(`proxy:${String(key)}`, this.propBecomeObservable.bind(this, target, key), this.propBecomeUnobservable.bind(this, target, key)),
                changed: false,
                method: new SyncedMethod(key, implVal !== null && implVal !== void 0 ? implVal : null),
            };
            (_c = this.syncProperties.get(target)) === null || _c === void 0 ? void 0 : _c.set(key, obj);
        }
        return obj;
    }
    getObservedProp(target, key) {
        const meta = this.getOrCrateMeta(target, key);
        meta.atom.reportObserved();
        return meta.val;
    }
    deleteObservedProp(target, key) {
        var _a;
        const meta = this.getOrCrateMeta(target, key);
        const impl = (_a = this.implementation.get(target)) === null || _a === void 0 ? void 0 : _a.impl;
        meta.val = impl == null ? undefined : Reflect.get(impl, key);
        meta.changed = false;
        meta.atom.reportObserved();
        return meta.val;
    }
    reportObservedProp(target, key, val) {
        const meta = this.getOrCrateMeta(target, key);
        meta.val = val;
        meta.changed = true;
        meta.atom.reportChanged();
    }
    getObservableImplementation(target) {
        let implMeta = this.implementation.get(target);
        if (implMeta == null) {
            implMeta = { atom: (0, mobx_1.createAtom)("proxy:implementation"), impl: null, sentWarn: false };
            this.implementation.set(target, implMeta);
        }
        if (!implMeta.atom.reportObserved() &&
            events_typed_1.EventEmitter.listenerCount(target, "implementation") === 0 &&
            !implMeta.sentWarn) {
            // vvv disabled for now because it requires players refactoring vvv
            //
            // const klass = isNamedClass(target.constructor) ? target.constructor.displayName : target.constructor.name;
            // this.logger.warn(
            //   "Warning! Attempt to use an implementation check in non-observable context and without listening `implementation`" +
            //     " event. The object may not reflect correct properties at the time. Please consider to use " +
            //     `\`autorun(() => ... )\` from MobX or \`obj.on('implementation', () => ... )\` event. Class: ${klass}`,
            // );
            implMeta.sentWarn = true;
        }
        return implMeta.impl;
    }
    setImplementation(target, impl) {
        var _a, _b, _c;
        for (const [key, meta] of (_b = (_a = this.syncProperties.get(target)) === null || _a === void 0 ? void 0 : _a.entries()) !== null && _b !== void 0 ? _b : []) {
            // we need to recreate reactions for all keys
            // so it will request changes from new impl after that
            // and update current values
            this.sync(target, meta, key, impl);
        }
        // notify mobx that implementationAtom has changed
        let implMeta = this.implementation.get(target);
        if (implMeta == null) {
            implMeta = {
                impl: null,
                atom: (0, mobx_1.createAtom)("proxy:implementation"),
                sentWarn: false,
            };
            this.implementation.set(target, implMeta);
        }
        // remove all events from old implementations and add it to new one
        if (implMeta.impl != null) {
            (_c = implMeta.impl) === null || _c === void 0 ? void 0 : _c.emit("detached");
            super.removeProxy(target, implMeta.impl);
        }
        if (impl != null) {
            super.addProxy(target, impl);
        }
        //
        // impl?.once("disposed", () => {
        //   setTimeout(() => {
        //     if (impl === this.implementation.get(target)?.impl) {
        //       this.setImplementation(target, null);
        //     }
        //   });
        // });
        implMeta.impl = impl;
        implMeta.atom.reportChanged();
        target.emit("implementation");
        if (impl != null) {
            impl === null || impl === void 0 ? void 0 : impl.emit("attached");
        }
    }
    getImplMethod(target, key) {
        var _a;
        const impl = (_a = this.implementation.get(target)) === null || _a === void 0 ? void 0 : _a.impl;
        if (impl != null) {
            const implMethod = Reflect.get(impl, key);
            if (implMethod != null && typeof implMethod === "function") {
                return implMethod;
            }
        }
        return undefined;
    }
    get(target, key, receiver) {
        if (key === events_handler_1.originalTarget) {
            return target;
        }
        if (key === exports.implementation) {
            return this.getObservableImplementation(target);
        }
        if (events_handler_1.emitterMethods.has(key)) {
            const method = events_handler_1.emitterMethods.get(key);
            if (method != null) {
                return this[method].bind(this, target);
            }
        }
        if (this.options.methods != null && key in this.options.methods) {
            const method = this.getImplMethod(target, key);
            switch (this.options.methods[key]) {
                case "stub":
                    return method !== null && method !== void 0 ? method : (() => undefined);
                case "optional":
                    return method;
                case "strict":
                    if (method == null) {
                        throw new Error(`No implementations of method '${String(key)}' found`);
                    }
                    return method;
                case "postpone":
                    return this.getOrCrateMeta(target, key).method.wrapper;
                default:
            }
        }
        // do not override existing props behaviour
        if (Reflect.has(target, key)) {
            return Reflect.get(target, key, receiver);
        }
        return this.getObservedProp(target, key);
    }
    set(target, prop, value, receiver) {
        if (prop === exports.implementation) {
            (0, mobx_1.runInAction)(() => {
                this.setImplementation(target, value);
            });
            return true;
        }
        // do not override existing props behaviour
        if (Reflect.has(target, prop)) {
            const result = Reflect.set(target, prop, value, receiver);
            if (!result) {
                const klass = (0, logger_1.isNamedClass)(target.constructor) ? target.constructor.displayName : target.constructor.name;
                this.logger.warn(`Property '${String(prop)}' of class '${klass}' is not writable`);
            }
            return true;
        }
        // always store incoming value in lazy properties
        this.reportObservedProp(target, prop, value);
        // pass to target
        const implMeta = this.implementation.get(target);
        if ((implMeta === null || implMeta === void 0 ? void 0 : implMeta.impl) != null) {
            const result = Reflect.set(implMeta.impl, prop, value);
            if (!result) {
                const klass = (0, logger_1.isNamedClass)(implMeta.impl.constructor)
                    ? implMeta.impl.constructor.displayName
                    : implMeta.impl.constructor.name;
                this.logger.warn(`Property '${String(prop)}' of class '${klass}' is not writable`);
            }
            return true;
        }
        return true;
    }
    deleteProperty(target, key) {
        if (key === exports.implementation) {
            this.setImplementation(target, null);
            return true;
        }
        // do not override existing props behaviour
        if (Reflect.has(target, key)) {
            return Reflect.deleteProperty(target, key);
        }
        this.deleteObservedProp(target, key);
        return true;
    }
}
exports.SyncHandler = SyncHandler;
//# sourceMappingURL=sync-handler.js.map